/**
 * PATH: packages/ui/src/components/settings/notifications-tab/NotificationPreferenceGroup.tsx
 * PURPOSE: Presentational sub-components for notification preference sections.
 * WHY: Extracted from NotificationsTab.tsx to keep the orchestrator under 300 lines
 *      and allow each UI piece to be tested/reused independently.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌────────────────────┐    ┌───────────┐
 *   │ Toggle (on/off)  │───▶│ NotificationSection │───▶│ SubOption │
 *   └──────────────────┘    └────────────────────┘    └───────────┘
 *
 * DEPENDENCIES:
 *   - ../../lib/utils: cn() class-name helper
 *   - ./notifications-tab-types: prop interfaces
 */

import { cn } from '../../../lib/utils'
import type {
  ToggleProps,
  NotificationSectionProps,
  SubOptionProps,
} from './notifications-tab-types'

// =============================================================================
// TOGGLE
// =============================================================================

export function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={cn(
        'relative w-11 h-6 rounded-full transition-colors',
        checked ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
          checked && 'translate-x-5'
        )}
      />
    </button>
  )
}

// =============================================================================
// NOTIFICATION SECTION
// =============================================================================

export function NotificationSection({
  icon,
  title,
  description,
  enabled,
  onToggle,
  disabled,
  children,
}: NotificationSectionProps) {
  return (
    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
            enabled ? 'bg-teal-500/10 text-teal-500' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'
          )}>
            {icon}
          </div>
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white">{title}</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
          </div>
        </div>
        <Toggle checked={enabled} onChange={onToggle} disabled={disabled} />
      </div>

      {enabled && children && (
        <div className="ml-13 pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-3">
          {children}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// SUB-OPTION
// =============================================================================

export function SubOption({ label, description, checked, onChange, disabled }: SubOptionProps) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <div>
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
        {description && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
        )}
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  )
}
